import React, { useEffect, useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { useAuth, loginWithGoogle } from "@/lib/auth";
import PublicLiveMap from "@/components/PublicLiveMap";
import { api } from "@/lib/api";
import {
  CarProfile,
  Car,
  Money,
  Path,
  Star,
  ShieldCheck,
  Lightning,
  GoogleLogo,
  ArrowRight,
  CurrencyDollar,
  MapPin,
} from "@phosphor-icons/react";

const PLACES = [
  "GEORGETOWN",
  "LINDEN",
  "NEW AMSTERDAM",
  "BARTICA",
  "ANNA REGINA",
  "LETHEM",
  "MABARUMA",
  "CORRIVERTON",
  "ROSE HALL",
  "PARIKA",
];

function CTA({ onSignIn }) {
  return (
    <div className="flex flex-col sm:flex-row gap-3">
      <Button
        data-testid="hero-signin-btn"
        onClick={onSignIn}
        className="rounded-none h-12 px-6 bg-foreground text-background hover:bg-foreground/90 font-semibold"
      >
        <GoogleLogo size={20} weight="bold" /> Continue with Google
      </Button>
      <Button
        data-testid="hero-learn-more-btn"
        variant="outline"
        className="rounded-none h-12 px-6 border-2 border-foreground hover:bg-secondary"
        onClick={() => {
          document.getElementById("how")?.scrollIntoView({ behavior: "smooth" });
        }}
      >
        How it works <ArrowRight size={18} />
      </Button>
    </div>
  );
}

function LiveDriverCount() {
  const [count, setCount] = useState(null);
  useEffect(() => {
    let cancelled = false;
    const fetch = async () => {
      try {
        const { data } = await api.get("/public/drivers-on-map");
        if (!cancelled) setCount(data.length);
      } catch {}
    };
    fetch();
    const t = setInterval(fetch, 5000);
    return () => { cancelled = true; clearInterval(t); };
  }, []);
  if (count == null) return null;
  return (
    <div
      data-testid="landing-live-count"
      className="absolute bottom-4 left-4 bg-background border-2 border-foreground px-3 py-2 z-[400]"
    >
      <div className="font-mono text-[10px] uppercase tracking-[0.2em] text-foreground/60">
        Drivers broadcasting
      </div>
      <div className="font-display text-2xl leading-none">
        {count}
      </div>
    </div>
  );
}

export default function Landing() {
  const navigate = useNavigate();
  const { user } = useAuth();

  const onSignIn = () => {
    if (user) {
      if (!user.role) navigate("/onboarding");
      else navigate(`/${user.role}`);
    } else {
      loginWithGoogle();
    }
  };

  return (
    <div className="min-h-screen bg-background text-foreground">
      {/* Top bar */}
      <header className="border-b-2 border-foreground">
        <div className="max-w-[1400px] mx-auto px-6 h-14 flex items-center justify-between">
          <div className="flex items-center gap-2 font-display text-xl" data-testid="landing-logo">
            <span className="inline-block w-3 h-3 bg-primary" />
            RideGY
          </div>
          <nav className="flex items-center gap-1">
            <a href="#how" className="hidden md:inline px-3 py-1.5 text-sm font-medium hover:underline">How it works</a>
            <a href="#drive" className="hidden md:inline px-3 py-1.5 text-sm font-medium hover:underline">Drive</a>
            <a href="#fare" className="hidden md:inline px-3 py-1.5 text-sm font-medium hover:underline">Fares</a>
            <Link
              to="/community"
              data-testid="landing-community-link"
              className="inline-flex items-center gap-1.5 px-3 py-1.5 mr-1 border-2 border-foreground hover:bg-foreground hover:text-background font-mono text-[11px] uppercase tracking-wider transition-colors"
            >
              <Car size={14} weight="fill" />
              <span className="hidden sm:inline">Ride Community</span>
              <span className="sm:hidden">Community</span>
            </Link>
            <Button
              data-testid="topbar-signin-btn"
              size="sm"
              onClick={onSignIn}
              className="rounded-none h-9 px-4 bg-foreground text-background hover:bg-foreground/90"
            >
              Sign in
            </Button>
          </nav>
        </div>
      </header>

      {/* Marquee of cities */}
      <div className="bg-foreground text-background border-b-2 border-foreground overflow-hidden">
        <div className="animate-marquee flex gap-10 whitespace-nowrap py-2 font-mono text-xs uppercase tracking-[0.3em] w-max">
          {[...PLACES, ...PLACES, ...PLACES].map((p, i) => (
            <span key={i} className="flex items-center gap-3">
              <span className="text-primary">●</span>
              {p}
            </span>
          ))}
        </div>
      </div>

      {/* Hero */}
      <section className="border-b-2 border-foreground">
        <div className="max-w-[1400px] mx-auto px-6 grid grid-cols-1 lg:grid-cols-12 gap-8 py-20 lg:py-28">
          <div className="lg:col-span-7 flex flex-col justify-center">
            <div className="font-mono text-xs uppercase tracking-[0.3em] text-foreground/60 mb-6">
              Guyana · 592 · Cash-Only Rides
            </div>
            <h1 className="font-display text-5xl sm:text-6xl lg:text-7xl leading-[0.95] tracking-tighter mb-6">
              The ride <br />
              <span className="bg-primary px-2 inline-block">that knows</span><br />
              Georgetown.
            </h1>
            <p className="text-lg max-w-xl text-foreground/80 mb-8 leading-relaxed">
              RideGY is a homegrown ride-hailing platform built for Guyana —
              transparent fixed fares, cash-only payments, and drivers who
              actually know the seawall, the stabroek market, and every street
              from Diamond to Parika.
            </p>
            <CTA onSignIn={onSignIn} />
            <div className="flex flex-wrap gap-4 mt-10 font-mono text-xs uppercase tracking-wider text-foreground/60">
              <span className="flex items-center gap-1.5"><ShieldCheck size={16} weight="bold" /> Verified drivers</span>
              <span className="flex items-center gap-1.5"><CurrencyDollar size={16} weight="bold" /> Cash only · GYD</span>
              <span className="flex items-center gap-1.5"><Lightning size={16} weight="bold" /> Instant pickup</span>
            </div>
          </div>
          <div className="lg:col-span-5">
            <div className="aspect-[4/5] w-full border-2 border-foreground overflow-hidden relative bg-secondary">
              <PublicLiveMap className="absolute inset-0" />
              <div className="absolute top-4 right-4 bg-primary border-2 border-foreground px-3 py-2 font-display text-2xl z-[400] shadow-[3px_3px_0_0_#111] hidden md:block">
                G$
              </div>
            </div>
            <div className="hidden lg:flex mt-4 items-center justify-between gap-3 border-2 border-foreground bg-background p-3 shadow-[4px_4px_0_0_#FFB800]">
              <div>
                <div className="font-mono text-[10px] uppercase tracking-wider text-foreground/60">Sample fare</div>
                <div className="font-display text-2xl leading-none mt-0.5">G$3,000</div>
              </div>
              <div className="text-xs text-foreground/70 text-right">Georgetown → New Amsterdam<br/>105 km · cash on arrival</div>
            </div>
          </div>
        </div>
      </section>

      {/* How it works */}
      <section id="how" className="border-b-2 border-foreground">
        <div className="max-w-[1400px] mx-auto px-6 py-20">
          <div className="grid grid-cols-1 md:grid-cols-12 gap-8 mb-12">
            <div className="md:col-span-5">
              <div className="font-mono text-xs uppercase tracking-[0.3em] text-foreground/60 mb-3">01 / Riders</div>
              <h2 className="font-display text-4xl lg:text-5xl tracking-tighter">
                Open. Tap. Ride.
              </h2>
            </div>
            <div className="md:col-span-7 text-foreground/80 text-lg leading-relaxed flex items-end">
              No surge pricing surprises. No card setup. Pick two points on the map and we calculate a fixed fare based on distance — pay your driver in cash when you arrive.
            </div>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-0 border-2 border-foreground">
            {[
              { icon: MapPin, title: "Set route", desc: "Tap pickup and drop-off directly on the map of Guyana." },
              { icon: Money, title: "See the fare", desc: "Fixed GYD fare shown before you book. Cash only — no cards." },
              { icon: CarProfile, title: "Match & go", desc: "Nearest verified driver is dispatched in seconds." },
            ].map((s, i) => (
              <div
                key={i}
                className={`p-8 ${i < 2 ? "border-b-2 md:border-b-0 md:border-r-2 border-foreground" : ""}`}
              >
                <s.icon size={36} weight="duotone" />
                <div className="font-mono text-xs uppercase tracking-wider text-foreground/60 mt-6">Step {i + 1}</div>
                <h3 className="font-display text-2xl mt-1">{s.title}</h3>
                <p className="text-foreground/70 mt-2">{s.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Drive */}
      <section id="drive" className="bg-foreground text-background border-b-2 border-foreground">
        <div className="max-w-[1400px] mx-auto px-6 grid grid-cols-1 md:grid-cols-12 gap-8 py-20">
          <div className="md:col-span-5">
            <div className="aspect-[4/3] border-2 border-background overflow-hidden">
              <img
                src="https://customer-assets.emergentagent.com/job_gyride-hub/artifacts/6t6n5wnm_icons.jpeg"
                alt="RideGY drivers parked at Sukhpaul's, Guyana"
                className="w-full h-full object-cover"
              />
            </div>
          </div>
          <div className="md:col-span-7 flex flex-col justify-center">
            <div className="font-mono text-xs uppercase tracking-[0.3em] text-background/60 mb-3">02 / Drivers</div>
            <h2 className="font-display text-4xl lg:text-5xl tracking-tighter mb-6">
              Drive on <span className="bg-primary text-foreground px-2">your hours</span>.
              Keep the cash.
            </h2>
            <p className="text-background/80 text-lg leading-relaxed mb-8 max-w-2xl">
              Riders pay you directly in GYD at the end of every trip. No
              hidden weekly cuts, no card processing fees. Toggle yourself
              online whenever you're ready to drive.
            </p>
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-px bg-background/30 border-2 border-background">
              {[
                ["100%", "Cash to driver"],
                ["0", "Card fees"],
                ["GYD", "Local currency"],
                ["24/7", "On your time"],
              ].map(([n, l], i) => (
                <div key={i} className="bg-foreground p-5">
                  <div className="font-display text-3xl">{n}</div>
                  <div className="font-mono text-xs uppercase tracking-wider text-background/60 mt-1">{l}</div>
                </div>
              ))}
            </div>
            <div className="mt-8">
              <Button
                data-testid="drive-signup-btn"
                onClick={onSignIn}
                className="rounded-none h-12 px-6 bg-primary text-foreground hover:bg-primary/90 font-semibold"
              >
                Become a driver <ArrowRight size={18} />
              </Button>
            </div>
          </div>
        </div>
      </section>

      {/* Fares */}
      <section id="fare" className="border-b-2 border-foreground">
        <div className="max-w-[1400px] mx-auto px-6 py-20 grid grid-cols-1 md:grid-cols-12 gap-8">
          <div className="md:col-span-4">
            <div className="font-mono text-xs uppercase tracking-[0.3em] text-foreground/60 mb-3">03 / Fares</div>
            <h2 className="font-display text-4xl lg:text-5xl tracking-tighter mb-6">
              Fixed fares.<br />Zero surprise.
            </h2>
            <p className="text-foreground/70">Our formula is public. Every ride uses the same math.</p>
          </div>
          <div className="md:col-span-8 border-2 border-foreground p-8 bg-card">
            <div className="font-mono text-xs uppercase tracking-wider text-foreground/60">Formula</div>
            <div className="font-display text-3xl md:text-4xl mt-2">
              FARE = G$500 + G$250 × KM
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 mt-8 border-t-2 border-foreground pt-6">
              {[
                ["2 km", "G$1,000"],
                ["5 km", "G$1,750"],
                ["10 km", "G$3,000"],
              ].map(([d, f], i) => (
                <div key={i} className="flex flex-col">
                  <span className="font-mono text-xs uppercase tracking-wider text-foreground/60">{d}</span>
                  <span className="font-display text-2xl">{f}</span>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="bg-background">
        <div className="max-w-[1400px] mx-auto px-6 py-10 flex flex-col md:flex-row items-start md:items-center justify-between gap-4">
          <div className="font-mono text-xs uppercase tracking-[0.3em] text-foreground/60">
            © RideGY · Made in Guyana
          </div>
          <div className="flex gap-6 text-sm">
            <a className="link-underline" href="#how">How</a>
            <a className="link-underline" href="#drive">Drive</a>
            <a className="link-underline" href="#fare">Fares</a>
            <button
              data-testid="footer-signin-btn"
              onClick={onSignIn}
              className="link-underline"
            >
              Sign in
            </button>
          </div>
        </div>
      </footer>
    </div>
  );
}
